import { ChangeDetectionStrategy, Component, computed, effect, inject, input } from '@angular/core';
import { StockLocation } from '@idealsupply/products-api-angular-client';
import { ImageComponent, LoadingSkeletonDirective2 } from 'common-ui';
import { ItemPriceComponentStore, ItemStockComponentStore, ProductDetailsComponentStore, ProductEntity } from 'products-data';
import { ItemPriceComponent, KeywordsInlineComponent, XrefInlineComponent } from 'products-ui';

const StockLocationTypeLocalized = {
  ['']: $localize`:@@product-fast-search.location.available-to-order:Available to Order`,
  [StockLocation.None]: $localize`:@@product-fast-search.location.none:Out of Stock`,
  [StockLocation.InStock]: $localize`:@@product-fast-search.location.in-stock:In Stock`,
  [StockLocation.InWarehouse]: $localize`:@@product-fast-search.location.in-warehouse:In Stock`,
  [StockLocation.OnBackOrder]: $localize`:@@product-fast-search.location.on-back-order:On Back Order`,
  [StockLocation.CanBackOrder]: $localize`:@@product-fast-search.location.can-back-order:Can Back Order`,
};

@Component({
  selector: 'ideal-product-quick-search-item',
  standalone: true,
  imports: [ImageComponent, LoadingSkeletonDirective2, ItemPriceComponent, KeywordsInlineComponent, XrefInlineComponent],
  templateUrl: './product-quick-search-item.component.html',
  styleUrl: './product-quick-search-item.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ProductDetailsComponentStore, ItemStockComponentStore, ItemPriceComponentStore],
})
export class ProductQuickSearchItemComponent {
  private readonly _detailStore = inject(ProductDetailsComponentStore);
  private readonly _stockStore = inject(ItemStockComponentStore);
  private readonly _priceStore = inject(ItemPriceComponentStore);

  public readonly product = input.required<ProductEntity>();
  public readonly searchTerm = input.required<string>();
  public readonly resultType = input.required<string>();

  protected readonly StockLocation = StockLocation;
  public readonly productDetail = this._detailStore.productDetails;
  public readonly imageSrc = computed(() => this.product()?.thumbnail ?? '');
  public readonly canadian = computed(() => this.productDetail()?.canadian ?? false);

  public constructor() {
    effect(() => this._detailStore.selectById(this.product()));
    effect(() => this._stockStore.selectProductById(this.product()));
    effect(() => this._priceStore.selectItemById(this.product()));
  }

  protected readonly stock = this._stockStore.itemStock;
  public readonly itemId = computed(() => this.stock()?.id);
  protected readonly stockLocation = computed(() => this.stock()?.stockLocation ?? StockLocation.None);
  protected readonly stockLabel = computed(
    () =>
      StockLocationTypeLocalized[this.stockLocation() as keyof typeof StockLocationTypeLocalized] ??
      StockLocationTypeLocalized[StockLocation.None],
  );
  protected readonly stockLoading = this.stock.loading;
  protected readonly priceLoading = computed(() => !this.product() || !this.itemId() || this._priceStore.itemPricing.loading());

  protected readonly partNumberMatched = computed(() =>
    this.resultType() === 'partNumber'
      ? this.highlightMatchedString(this.searchTerm(), this.product().id.partNumber)
      : this.product().id.partNumber,
  );

  protected readonly descriptionMatched = computed(() =>
    this.resultType() === 'description'
      ? this.highlightMatchedString(this.searchTerm(), this.product().description ?? '')
      : this.product().description,
  );

  protected readonly keywordsMatched = computed(() => [] as string[]);
  protected readonly crossRefsMatched = computed(() => [] as string[]);

  private highlightMatchedString(search: string, matches: string): string {
    const searchFor = search.trim().toLowerCase();
    const searchIn = matches.trim().toLowerCase();

    const idx = searchIn.indexOf(searchFor);
    if (idx === -1) {
      return matches;
    }

    const one = matches.substring(0, idx);
    const two = matches.substring(idx, idx + searchFor.length);
    const three = matches.substring(idx + searchFor.length);
    return `${one}<span class="search-match">${two}</span>${three}`;
  }
}
